import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import Modal from '../components/Modal';
import { PrintNotesField, InvoiceNotesEditor } from '../components/InvoiceNotes';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  // Handle both "2026-04-01" and "2026-04-01 14:04:18" formats
  const normalized = dateStr.includes('T') ? dateStr : dateStr.includes(' ') ? dateStr.replace(' ', 'T') : dateStr + 'T00:00:00';
  return new Date(normalized).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
}

function getPreviousMonthDates() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    period_start: firstDay.toISOString().split('T')[0],
    period_end: lastDay.toISOString().split('T')[0],
  };
}

function formatLocalDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function getShiftedMonthDates(fromStart, monthDelta) {
  const base = fromStart ? new Date(`${fromStart}T00:00:00`) : new Date();
  const firstDay = new Date(base.getFullYear(), base.getMonth() + monthDelta, 1);
  const lastDay = new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 0);
  return { start: formatLocalDate(firstDay), end: formatLocalDate(lastDay) };
}

function getCurrentMonthDates() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    period_start: firstDay.toISOString().split('T')[0],
    period_end: lastDay.toISOString().split('T')[0],
  };
}

function getDefaultDates() {
  return getPreviousMonthDates();
}

function getDueDate(invoiceDate, paymentTerms = 'Net 30') {
  const normalized = typeof invoiceDate === 'string' && invoiceDate.includes(' ') ? invoiceDate.replace(' ', 'T') : invoiceDate;
  const d = new Date(normalized);

  // Parse payment terms to get number of days
  let days = 30; // default
  if (paymentTerms === 'Immediate') {
    days = 0;
  } else {
    const match = paymentTerms?.match(/Net\s*(\d+)/i);
    if (match) {
      days = parseInt(match[1], 10);
    }
  }

  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

const emptyPayment = { amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: '', reference_number: '', notes: '' };
const PAYMENT_METHODS = ['Check', 'ACH/Wire', 'Credit Card', 'Cash', 'Other'];

function SubmissionStatusTab({ submissionMonth, setSubmissionMonth, submissionStatus, loadingStatus }) {
  const [customerFilter, setCustomerFilter] = useState('');
  const [engineerFilter, setEngineerFilter] = useState('');
  const [reminderSelections, setReminderSelections] = useState({});
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [monthConfirmations, setMonthConfirmations] = useState(null);

  useEffect(() => {
    if (!submissionMonth) return;
    apiFetch(`/month-confirmations/summary?month=${submissionMonth}`)
      .then(data => setMonthConfirmations(data))
      .catch(() => setMonthConfirmations(null));
  }, [submissionMonth]);

  if (loadingStatus) return <div style={{ padding: 40, color: '#94a3b8' }}>Loading submission status...</div>;

  const monthLabel = submissionStatus ? new Date(submissionStatus.month + '-15').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '';

  const allEngineers = submissionStatus?.engineers || [];

  // Get unique customers and engineers for filter dropdowns
  const uniqueCustomers = [...new Set(allEngineers.map(e => e.customer_name))].sort();
  const uniqueEngineers = [...new Map(allEngineers.map(e => [e.user_id, e.engineer_name])).values()].sort();

  // Apply filters
  const filteredEngineers = allEngineers.filter(e => {
    if (customerFilter && e.customer_name !== customerFilter) return false;
    if (engineerFilter && e.engineer_name !== engineerFilter) return false;
    return true;
  });

  // Separate weekly vs monthly engineers
  const weeklyEngineers = filteredEngineers.filter(e => e.include_timesheets !== 0);
  const monthlyEngineers = filteredEngineers.filter(e => e.include_timesheets === 0);
  const weeks = submissionStatus?.weeks || [];

  // Count stats
  const totalExpected = weeklyEngineers.length * weeks.length + monthlyEngineers.length;
  const totalSubmitted = weeklyEngineers.reduce((sum, eng) => sum + Object.keys(eng.weeks).length, 0)
    + monthlyEngineers.filter(eng => Object.keys(eng.weeks).length > 0).length;

  return (
    <div>
      {/* Month selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => {
            const [y, m] = submissionMonth.split('-').map(Number);
            const prev = new Date(y, m - 2, 1);
            setSubmissionMonth(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`);
          }}
          style={{ fontSize: 18, padding: '4px 12px', lineHeight: 1 }}
        >&larr;</button>
        <span style={{ fontWeight: 600, fontSize: 16, minWidth: 160, textAlign: 'center' }}>
          {new Date(submissionMonth + '-15').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => {
            const [y, m] = submissionMonth.split('-').map(Number);
            const next = new Date(y, m, 1);
            setSubmissionMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`);
          }}
          style={{ fontSize: 18, padding: '4px 12px', lineHeight: 1 }}
        >&rarr;</button>
        {submissionStatus && (
          <span style={{ fontSize: 13, color: '#64748b' }}>
            {totalSubmitted} / {totalExpected} submissions received
          </span>
        )}
      </div>

      {monthConfirmations && monthConfirmations.total_engineers > 0 && (
        <div style={{ background: monthConfirmations.confirmed_count === monthConfirmations.total_engineers ? '#f0fdf4' : '#fffbeb', border: `1px solid ${monthConfirmations.confirmed_count === monthConfirmations.total_engineers ? '#86efac' : '#fde68a'}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: monthConfirmations.confirmed_count === monthConfirmations.total_engineers ? '#166534' : '#92400e' }}>
              Month Confirmations: {monthConfirmations.confirmed_count} / {monthConfirmations.total_engineers} engineers confirmed
            </span>
          </div>
          {monthConfirmations.engineers.some(e => e.confirmed_at) && (
            <div style={{ fontSize: 12, color: '#166534', marginBottom: monthConfirmations.engineers.some(e => !e.confirmed_at) ? 6 : 0 }}>
              Confirmed: {monthConfirmations.engineers.filter(e => e.confirmed_at).map(e => {
                const d = new Date(e.confirmed_at);
                return `${e.name} (${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
              }).join(', ')}
            </div>
          )}
          {monthConfirmations.engineers.some(e => !e.confirmed_at) && (
            <div style={{ fontSize: 12, color: '#92400e' }}>
              Outstanding: {monthConfirmations.engineers.filter(e => !e.confirmed_at).map(e => e.name).join(', ')}
            </div>
          )}
        </div>
      )}

      {submissionStatus && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Customer:</span>
            <select
              className="form-select"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 180 }}
            >
              <option value="">All Customers</option>
              {uniqueCustomers.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Engineer:</span>
            <select
              className="form-select"
              value={engineerFilter}
              onChange={(e) => setEngineerFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 180 }}
            >
              <option value="">All Engineers</option>
              {uniqueEngineers.map(e => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {!submissionStatus ? (
        <div className="empty-state"><p>Select a month to view submission status.</p></div>
      ) : (
        <>
          {/* Weekly timesheet engineers */}
          {weeklyEngineers.length > 0 && (() => {
            const engineersWithMissing = weeklyEngineers.filter(eng => !weeks.every(w => eng.weeks[w]));
            const allMissingSelected = engineersWithMissing.length > 0 && engineersWithMissing.every(eng => reminderSelections[`${eng.user_id}-${eng.project_id}`]);

            const handleSendReminders = async () => {
              const selected = engineersWithMissing.filter(eng => reminderSelections[`${eng.user_id}-${eng.project_id}`]);
              if (selected.length === 0) return;
              setSendingReminders(true);
              setReminderResult(null);
              try {
                const recipients = selected.map(eng => ({
                  email: eng.engineer_email,
                  engineer_name: eng.engineer_name,
                  project_name: eng.project_name,
                  missing_weeks: weeks.filter(w => !eng.weeks[w]),
                }));
                const result = await apiFetch('/timesheet-reminders', { method: 'POST', body: { recipients, month: submissionMonth } });
                setReminderResult({ success: true, count: result.sent });
                setReminderSelections({});
              } catch (e) {
                setReminderResult({ success: false, error: e.message });
              } finally {
                setSendingReminders(false);
              }
            };

            return (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Requires Timesheets</div>
                {engineersWithMissing.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {Object.values(reminderSelections).some(v => v) && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSendReminders}
                        disabled={sendingReminders}
                      >
                        {sendingReminders ? 'Sending...' : `Send Reminder (${Object.values(reminderSelections).filter(v => v).length})`}
                      </button>
                    )}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        if (allMissingSelected) {
                          setReminderSelections({});
                        } else {
                          const next = {};
                          engineersWithMissing.forEach(eng => { next[`${eng.user_id}-${eng.project_id}`] = true; });
                          setReminderSelections(next);
                        }
                      }}
                    >
                      {allMissingSelected ? 'Deselect All' : 'Select All Missing'}
                    </button>
                  </div>
                )}
              </div>
              {reminderResult && (
                <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, fontSize: 13, background: reminderResult.success ? '#dcfce7' : '#fef2f2', color: reminderResult.success ? '#16a34a' : '#dc2626' }}>
                  {reminderResult.success ? `Sent ${reminderResult.count} reminder(s) successfully.` : `Error: ${reminderResult.error}`}
                </div>
              )}
              <div className="table-wrap">
                <table style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}></th>
                      <th style={{ position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1 }}>Engineer</th>
                      <th>Project</th>
                      {weeks.map(w => (
                        <th key={w} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {new Date(w + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyEngineers.map(eng => {
                      const allSubmitted = weeks.every(w => eng.weeks[w]);
                      const key = `${eng.user_id}-${eng.project_id}`;
                      return (
                        <tr key={key} style={{ background: allSubmitted ? '#f0f9ff' : undefined }}>
                          <td>
                            {!allSubmitted && (
                              <input
                                type="checkbox"
                                checked={!!reminderSelections[key]}
                                onChange={(e) => setReminderSelections({ ...reminderSelections, [key]: e.target.checked })}
                                style={{ width: 14, height: 14 }}
                              />
                            )}
                          </td>
                          <td style={{ position: 'sticky', left: 0, background: allSubmitted ? '#f0f9ff' : '#fff', zIndex: 1, fontWeight: 500 }}>
                            {eng.engineer_name}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', color: '#64748b', fontSize: 11 }}>{eng.project_name}</td>
                          {weeks.map(w => {
                            const ts = eng.weeks[w];
                            if (ts) {
                              return (
                                <td key={w} style={{ textAlign: 'center' }}>
                                  <span
                                    title={`${ts.status} - ${parseFloat(ts.total_hours.toFixed(2))}h`}
                                    style={{
                                      display: 'inline-block',
                                      padding: '2px 6px',
                                      borderRadius: 4,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      background: ts.status === 'approved' ? '#dbeafe' : '#fef3c7',
                                      color: ts.status === 'approved' ? '#1d4ed8' : '#92400e',
                                    }}
                                  >
                                    {parseFloat(ts.total_hours.toFixed(2))}h
                                  </span>
                                </td>
                              );
                            }
                            return (
                              <td key={w} style={{ textAlign: 'center' }}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: '#fef9c3',
                                  color: '#854d0e',
                                }}>
                                  Missing
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })()}

          {/* Does not require timesheets */}
          {monthlyEngineers.length > 0 && (
            <div className="card">
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Does Not Require Timesheets</div>
              <div className="table-wrap">
                <table style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Engineer</th>
                      <th>Project</th>
                      <th>Type</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th style={{ textAlign: 'right' }}>Amount / Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyEngineers.map(eng => {
                      const submissions = Object.values(eng.weeks);
                      const hasSubmission = submissions.length > 0;
                      const totalHours = submissions.reduce((s, ts) => s + (ts.total_hours || 0), 0);
                      const totalAmount = submissions.reduce((s, ts) => s + (ts.amount || 0), 0);
                      const allApproved = hasSubmission && submissions.every(ts => ts.status === 'approved');
                      return (
                        <tr key={`${eng.user_id}-${eng.project_id}`} style={{ background: hasSubmission ? '#f0f9ff' : undefined }}>
                          <td style={{ fontWeight: 500 }}>{eng.engineer_name}</td>
                          <td style={{ color: '#64748b', fontSize: 11 }}>{eng.project_name}</td>
                          <td style={{ fontSize: 11, textTransform: 'capitalize' }}>{eng.project_type.replace('_', ' ')}</td>
                          <td style={{ textAlign: 'center' }}>
                            {hasSubmission ? (
                              <span style={{
                                padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                                background: allApproved ? '#dbeafe' : '#fef3c7',
                                color: allApproved ? '#1d4ed8' : '#92400e',
                              }}>{allApproved ? 'Approved' : 'Submitted'}</span>
                            ) : (
                              <span style={{
                                padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                                background: '#fef9c3', color: '#854d0e',
                              }}>Missing</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace' }}>
                            {hasSubmission ? (
                              totalAmount > 0 ? formatCurrency(totalAmount) : `${parseFloat(totalHours.toFixed(2))}h`
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {weeklyEngineers.length === 0 && monthlyEngineers.length === 0 && (
            <div className="empty-state">
              <h3>No active engineer assignments</h3>
              <p>No engineers are assigned to active projects for this period.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [balances, setBalances] = useState({ total_outstanding: 0, by_customer: [], by_project: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [generateForm, setGenerateForm] = useState({ project_id: '', ...getDefaultDates(), print_notes: '' });
  const [manualForm, setManualForm] = useState({ project_id: '', total_amount: '', description: '', period_start: '', period_end: '', notes: '', print_notes: '' });
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [editingAmount, setEditingAmount] = useState(null);
  const [paymentForm, setPaymentForm] = useState(emptyPayment);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [projectStatusFilter, setProjectStatusFilter] = useState('active');
  const [customerFilter, setCustomerFilter] = useState('');
  const [engineerFilter, setEngineerFilter] = useState('');
  const [receivedFilter, setReceivedFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [engineers, setEngineers] = useState([]);
  const [engineerAssignments, setEngineerAssignments] = useState([]);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [emailingId, setEmailingId] = useState(null);
  const [activeTab, setActiveTab] = useState('invoices');
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submissionMonth, setSubmissionMonth] = useState(() => {
    const now = new Date();
    // Default to previous month
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Batch invoice generator state
  const [batchPeriod, setBatchPeriod] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  });
  const [readyProjects, setReadyProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState({});
  const [findingProjects, setFindingProjects] = useState(false);
  const [generatingBatch, setGeneratingBatch] = useState(false);
  const [showBatchGenerator, setShowBatchGenerator] = useState(false);

  // Batch payment state
  const [showBatchPayment, setShowBatchPayment] = useState(false);
  const [batchPayCustomer, setBatchPayCustomer] = useState('');
  const [batchPaySelected, setBatchPaySelected] = useState({});
  const [batchPayForm, setBatchPayForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    reference_number: '',
    notes: '',
  });
  const [batchPaySaving, setBatchPaySaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [showAdviceImport, setShowAdviceImport] = useState(false);
  const [adviceResults, setAdviceResults] = useState(null);
  const [adviceSelected, setAdviceSelected] = useState({});
  const [adviceProcessing, setAdviceProcessing] = useState(false);
  const [ediUploadResults, setEdiUploadResults] = useState(null);
  const [ediUploading, setEdiUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [inv, proj, bal, users, ep, cust] = await Promise.all([
        apiFetch('/invoices'),
        apiFetch('/projects'),
        apiFetch('/balances'),
        apiFetch('/users'),
        apiFetch('/engineer-projects'),
        apiFetch('/customers'),
      ]);
      setInvoices(inv);
      setProjects(proj);
      setBalances(bal);
      setEngineers(users.filter(u => u.role === 'engineer'));
      setEngineerAssignments(ep);
      setCustomers(cust);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissionStatus = async (month) => {
    setLoadingStatus(true);
    try {
      const data = await apiFetch(`/submission-status?month=${month}`);
      setSubmissionStatus(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'status') {
      loadSubmissionStatus(submissionMonth);
    }
  }, [activeTab, submissionMonth]);

  const openGenerate = () => {
    setGenerateForm({ project_id: '', ...getDefaultDates(), print_notes: '' });
    setError('');
    setModal('generate');
  };

  const openManual = () => {
    setManualForm({ project_id: '', total_amount: '', description: '', period_start: '', period_end: '', notes: '', print_notes: '' });
    setError('');
    setModal('manual');
  };

  const handleManualInvoice = async (e) => {
    e.preventDefault();
    if (!manualForm.project_id || !manualForm.total_amount) {
      setError('Project and amount are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await apiFetch('/invoices/manual', {
        method: 'POST',
        body: { ...manualForm, total_amount: parseFloat(manualForm.total_amount) },
      });
      setViewingInvoice(result);
      await loadData();
      setModal('view');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!generateForm.project_id || !generateForm.period_start || !generateForm.period_end) {
      setError('All fields are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await apiFetch('/invoices/generate', {
        method: 'POST',
        body: generateForm,
      });
      setViewingInvoice(result);
      await loadData();
      setModal('view');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const findReadyProjects = async () => {
    setFindingProjects(true);
    setError('');
    try {
      const data = await apiFetch(`/invoices/find-ready?period_start=${batchPeriod.start}&period_end=${batchPeriod.end}`);
      setReadyProjects(data);
      setSelectedProjects({});
    } catch (e) {
      setError(e.message);
    } finally {
      setFindingProjects(false);
    }
  };

  const toggleProjectSelection = (projectId) => {
    setSelectedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const selectAllProjects = () => {
    const all = {};
    readyProjects.forEach(p => {
      if (!p.over_budget) all[p.id] = true;
    });
    setSelectedProjects(all);
  };

  const selectNoneProjects = () => {
    setSelectedProjects({});
  };

  const generateBatchInvoices = async () => {
    const projectIds = Object.keys(selectedProjects).filter(id => selectedProjects[id]);
    if (projectIds.length === 0) {
      setError('Please select at least one project');
      return;
    }

    setGeneratingBatch(true);
    setError('');
    let successCount = 0;
    const errors = [];

    for (const projectId of projectIds) {
      const proj = readyProjects.find(p => String(p.id) === String(projectId));
      try {
        await apiFetch('/invoices/generate', {
          method: 'POST',
          body: {
            project_id: projectId,
            period_start: batchPeriod.start,
            period_end: batchPeriod.end,
            notes: ''
          }
        });
        successCount++;
      } catch (e) {
        console.error(`Failed to generate invoice for project ${projectId}:`, e);
        errors.push(`${proj?.project_name || projectId}: ${e.message}`);
      }
    }

    await loadData();
    setReadyProjects([]);
    setSelectedProjects({});
    setGeneratingBatch(false);

    if (errors.length > 0) {
      alert(`Generated ${successCount} invoice(s). ${errors.length} failed:\n\n${errors.join('\n')}`);
    } else {
      alert(`Successfully generated ${successCount} invoice(s).`);
    }
  };

  const viewInvoice = async (inv) => {
    try {
      const data = await apiFetch(`/invoices/${inv.id}`);
      setViewingInvoice(data);
      setModal('view');
    } catch (e) {
      alert('Error loading invoice: ' + e.message);
    }
  };

  const handleDelete = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Are you sure you want to delete Invoice #${invoiceNumber}? This cannot be undone.`)) {
      return;
    }
    try {
      await apiFetch(`/invoices/${invoiceId}`, { method: 'DELETE' });
      await loadData();
      if (viewingInvoice?.id === invoiceId) {
        setModal(null);
        setViewingInvoice(null);
      }
    } catch (e) {
      alert('Error deleting invoice: ' + e.message);
    }
  };

  const handleRegenerate = async () => {
    if (!viewingInvoice) return;
    if (!window.confirm('This will delete the current invoice and generate a new one with updated data. The invoice number will change. Continue?')) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Store the parameters from the current invoice
      const params = {
        project_id: viewingInvoice.project_id,
        period_start: viewingInvoice.period_start,
        period_end: viewingInvoice.period_end,
        notes: viewingInvoice.notes || '',
        print_notes: viewingInvoice.print_notes || '',
      };

      // Delete the old invoice
      await apiFetch(`/invoices/${viewingInvoice.id}`, { method: 'DELETE' });

      // Generate new invoice with same parameters
      const result = await apiFetch('/invoices/generate', {
        method: 'POST',
        body: params,
      });

      setViewingInvoice(result);
      await loadData();
    } catch (e) {
      setError(e.message);
      alert('Error regenerating invoice: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = async (invoice) => {
    if (!window.confirm(`Email Invoice #${invoice.invoice_number} to the customer's AP email?`)) return;
    setEmailingId(invoice.id);
    try {
      const result = await apiFetch(`/invoices/${invoice.id}/email`, { method: 'POST' });
      alert(result.message || 'Invoice emailed successfully!');
      await loadData(); // Refresh to show emailed indicator
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setEmailingId(null);
    }
  };

  const openPayment = async (invoice) => {
    setViewingInvoice(invoice);
    const balance = (invoice.total_amount || 0) - (invoice.amount_paid || 0);
    setPaymentForm({ ...emptyPayment, amount: balance.toFixed(2) });
    try {
      const pmts = await apiFetch(`/invoices/${invoice.id}/payments`);
      setPayments(pmts);
    } catch (e) {
      setPayments([]);
    }
    setError('');
    setModal('payment');
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/invoices/${viewingInvoice.id}/payments`, {
        method: 'POST',
        body: { ...paymentForm, amount: parseFloat(paymentForm.amount) },
      });
      await loadData();
      // Refresh payments list
      const pmts = await apiFetch(`/invoices/${viewingInvoice.id}/payments`);
      setPayments(pmts);
      // Refresh the invoice data
      const updatedInv = await apiFetch(`/invoices/${viewingInvoice.id}`);
      setViewingInvoice(updatedInv);
      setPaymentForm({ ...emptyPayment, amount: '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await apiFetch(`/payments/${paymentId}`, { method: 'DELETE' });
      await loadData();
      const pmts = await apiFetch(`/invoices/${viewingInvoice.id}/payments`);
      setPayments(pmts);
      const updatedInv = await apiFetch(`/invoices/${viewingInvoice.id}`);
      setViewingInvoice(updatedInv);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleVoid = async (invoice) => {
    const reason = window.prompt(`Void Invoice #${invoice.invoice_number}?\n\nThis will exclude it from outstanding balances.\n\nEnter a reason (optional):`);
    if (reason === null) return;
    try {
      await apiFetch(`/invoices/${invoice.id}/void`, { method: 'PUT', body: { reason: reason || '' } });
      await loadData();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleUnvoid = async (invoice) => {
    if (!window.confirm(`Restore Invoice #${invoice.invoice_number}?`)) return;
    try {
      await apiFetch(`/invoices/${invoice.id}/unvoid`, { method: 'PUT' });
      await loadData();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // Batch payment functions
  const outstandingForCustomer = invoices.filter(inv => {
    const status = inv.status || 'unpaid';
    return (status === 'unpaid' || status === 'partial' || status === 'draft') &&
      inv.customer_name === batchPayCustomer;
  });

  const batchPayTotal = outstandingForCustomer
    .filter(inv => batchPaySelected[inv.id])
    .reduce((sum, inv) => sum + ((inv.total_amount || 0) - (inv.amount_paid || 0)), 0);

  const toggleBatchPayInvoice = (id) => {
    setBatchPaySelected(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const selectAllBatchPay = () => {
    const all = {};
    outstandingForCustomer.forEach(inv => { all[inv.id] = true; });
    setBatchPaySelected(all);
  };

  const selectNoneBatchPay = () => {
    setBatchPaySelected({});
  };

  const handleBatchPayment = async () => {
    const selectedIds = Object.keys(batchPaySelected).filter(id => batchPaySelected[id]).map(Number);
    if (selectedIds.length === 0) {
      setError('Please select at least one invoice');
      return;
    }
    if (!batchPayForm.payment_date) {
      setError('Payment date is required');
      return;
    }
    if (!window.confirm(`Record payment for ${selectedIds.length} invoice(s) totaling ${formatCurrency(batchPayTotal)}?`)) {
      return;
    }
    setBatchPaySaving(true);
    setError('');
    try {
      const result = await apiFetch('/invoices/batch-payment', {
        method: 'POST',
        body: {
          invoice_ids: selectedIds,
          payment_date: batchPayForm.payment_date,
          payment_method: batchPayForm.payment_method,
          reference_number: batchPayForm.reference_number,
          notes: batchPayForm.notes,
        },
      });
      await loadData();
      setBatchPaySelected({});
      const paidCount = result.paid?.length || 0;
      const errCount = result.errors?.length || 0;
      if (errCount > 0) {
        alert(`Paid ${paidCount} invoice(s). ${errCount} had errors:\n${result.errors.map(e => `#${e.invoice_number}: ${e.error}`).join('\n')}`);
      } else {
        alert(`Successfully recorded payment for ${paidCount} invoice(s).`);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBatchPaySaving(false);
    }
  };

  const handleAdviceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('tt_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/invoices/import-payment-advice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setAdviceResults(data);
      const preSelected = {};
      data.matches.forEach(m => {
        if (!m.already_paid && m.balance_due > 0) preSelected[m.invoice_id] = true;
      });
      setAdviceSelected(preSelected);
    } catch (err) {
      alert('Error: ' + err.message);
    }
    e.target.value = '';
  };

  const handleAdviceApply = async () => {
    const selectedIds = Object.keys(adviceSelected).filter(id => adviceSelected[id]).map(Number);
    if (selectedIds.length === 0) {
      alert('No invoices selected');
      return;
    }
    const matched = adviceResults.matches.filter(m => selectedIds.includes(m.invoice_id));
    const hasPartial = matched.some(m => m.is_partial);
    const total = matched.reduce((s, m) => s + m.advice_amount, 0);
    const confirmMsg = hasPartial
      ? `Apply payments for ${selectedIds.length} invoice(s) totaling ${formatCurrency(total)}?\n\nNote: Some invoices have partial payments or deductions and will be marked as "partial" until the remaining balance is paid.`
      : `Mark ${selectedIds.length} invoice(s) as paid totaling ${formatCurrency(total)}?`;
    if (!window.confirm(confirmMsg)) return;

    setAdviceProcessing(true);
    try {
      const paymentDate = matched[0]?.payment_date || new Date().toISOString().split('T')[0];
      const paymentAmounts = {};
      matched.forEach(m => { paymentAmounts[m.invoice_id] = m.advice_amount; });
      const result = await apiFetch('/invoices/batch-payment', {
        method: 'POST',
        body: {
          invoice_ids: selectedIds,
          payment_date: paymentDate,
          payment_method: 'ACH',
          reference_number: adviceResults.paymentInfo?.document_number || 'Payment Advice Import',
          notes: adviceResults.paymentInfo ? `${adviceResults.paymentInfo.source} payment doc ${adviceResults.paymentInfo.document_number}` : 'Imported from payment advice file',
          payment_amounts: paymentAmounts,
        },
      });
      await loadData();
      const paidCount = result.paid?.length || 0;
      const partialCount = (result.paid || []).filter(p => p.status === 'partial').length;
      const msg = partialCount > 0
        ? `Recorded payments for ${paidCount} invoice(s): ${paidCount - partialCount} paid in full, ${partialCount} partial.`
        : `Successfully recorded payment for ${paidCount} invoice(s).`;
      alert(msg);
      setAdviceResults(null);
      setAdviceSelected({});
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setAdviceProcessing(false);
    }
  };

  const handleDownloadEdi810 = async (invoice) => {
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('tt_token');
      const response = await fetch(`${base}/api/invoices/${invoice.id}/edi-810`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to generate EDI 810');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.headers.get('content-disposition')?.match(/filename="(.+)"/)?.[1] || `810_${invoice.invoice_number}.edi`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('EDI 810 Error: ' + e.message);
    }
  };


  const handleEdiResponseUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setEdiUploading(true);
    setEdiUploadResults(null);
    try {
      const base = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('tt_token');
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      const response = await fetch(`${base}/api/invoices/upload-edi-response`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to upload EDI response files');
      }
      const data = await response.json();
      setEdiUploadResults(data);
      loadData();
    } catch (err) {
      alert('EDI Upload Error: ' + err.message);
    } finally {
      setEdiUploading(false);
      e.target.value = '';
    }
  };

  const handleReceived = async (invoice) => {
    try {
      if (invoice.received_at) {
        await apiFetch(`/invoices/${invoice.id}/unreceived`, { method: 'PUT' });
      } else {
        await apiFetch(`/invoices/${invoice.id}/received`, { method: 'PUT' });
      }
      await loadData();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  // Filter invoices
  // Normalize status: treat null/undefined/empty/'draft' as 'unpaid'
  const getStatus = (inv) => {
    const status = inv.status || 'unpaid';
    return status === 'draft' ? 'unpaid' : status;
  };

  // Years that have invoices, newest first, always including the current year
  const invoiceYears = [...new Set([String(new Date().getFullYear()), ...invoices.map(inv => (inv.created_at || '').substring(0, 4)).filter(Boolean)])].sort().reverse();

  // Get unique customers for filter dropdown
  const uniqueCustomers = [...new Map(invoices.map(inv => [inv.customer_name, { name: inv.customer_name }])).values()].sort((a, b) => a.name.localeCompare(b.name));

  let filteredInvoices = invoices;
  if (statusFilter === 'active') {
    filteredInvoices = invoices.filter(inv => {
      const status = getStatus(inv);
      return status === 'unpaid' || status === 'partial';
    });
  } else if (statusFilter) {
    filteredInvoices = invoices.filter(inv => getStatus(inv) === statusFilter);
  }
  // Apply customer filter
  if (customerFilter) {
    filteredInvoices = filteredInvoices.filter(inv => inv.customer_name === customerFilter);
  }
  // Apply engineer filter (filter by projects that have the engineer assigned)
  if (engineerFilter) {
    const projectIdsWithEngineer = engineerAssignments
      .filter(ea => String(ea.user_id) === engineerFilter)
      .map(ea => ea.project_id);
    filteredInvoices = filteredInvoices.filter(inv => projectIdsWithEngineer.includes(inv.project_id));
  }
  // Apply year filter (by invoice date) but always show unpaid/partial invoices from earlier years
  if (yearFilter) {
    filteredInvoices = filteredInvoices.filter(inv => {
      const status = getStatus(inv);
      if (status === 'unpaid' || status === 'partial') return true;
      return (inv.created_at || '').startsWith(yearFilter);
    });
  }
  // Apply received filter
  if (receivedFilter === 'received') {
    filteredInvoices = filteredInvoices.filter(inv => inv.received_at);
  } else if (receivedFilter === 'not_received') {
    filteredInvoices = filteredInvoices.filter(inv => !inv.received_at);
  }
  // Apply project status filter (active/inactive) but always show unpaid/partial invoices
  if (projectStatusFilter) {
    const matchingProjectIds = new Set(projects.filter(p => p.status === projectStatusFilter).map(p => p.id));
    filteredInvoices = filteredInvoices.filter(inv => {
      const status = getStatus(inv);
      if (status === 'unpaid' || status === 'partial') return true;
      return matchingProjectIds.has(inv.project_id);
    });
  }

  // Sort invoices
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case 'invoice_number':
        aVal = parseInt(a.invoice_number) || 0;
        bVal = parseInt(b.invoice_number) || 0;
        break;
      case 'customer_name':
        aVal = (a.customer_name || '').toLowerCase();
        bVal = (b.customer_name || '').toLowerCase();
        break;
      case 'created_at':
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
        break;
      case 'total_amount':
        aVal = a.total_amount || 0;
        bVal = b.total_amount || 0;
        break;
      case 'balance':
        aVal = (a.total_amount || 0) - (a.amount_paid || 0);
        bVal = (b.total_amount || 0) - (b.amount_paid || 0);
        break;
      case 'due_date':
        const getDueDate = (inv) => {
          if (!inv.created_at) return 0;
          const days = parseInt((inv.payment_terms || 'Net 30').replace(/\D/g, '')) || 30;
          return new Date(inv.created_at).getTime() + days * 86400000;
        };
        aVal = getDueDate(a);
        bVal = getDueDate(b);
        break;
      default:
        aVal = a[sortField];
        bVal = b[sortField];
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortHeader = ({ field, children }) => (
    <th
      onClick={() => handleSort(field)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {children}
      {sortField === field && (
        <span style={{ marginLeft: 4, fontSize: 10 }}>
          {sortDir === 'asc' ? '▲' : '▼'}
        </span>
      )}
    </th>
  );

  if (loading) return <div style={{ padding: 40, color: '#94a3b8' }}>Loading...</div>;

  const inv = viewingInvoice;
  const settings = inv?.settings || {};

  const getStatusBadge = (status) => {
    const styles = {
      unpaid: { background: '#fef3c7', color: '#92400e' },
      partial: { background: '#dbeafe', color: '#1e40af' },
      paid: { background: '#d1fae5', color: '#065f46' },
      voided: { background: '#f3f4f6', color: '#6b7280', textDecoration: 'line-through' },
    };
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        ...styles[status]
      }}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="page-header no-print">
        <div>
          <div className="page-title">Invoices</div>
          <div className="page-subtitle">Generate and manage invoices</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-primary" onClick={openGenerate}>+ Generate Invoice</button>
          <button className="btn btn-secondary" onClick={openManual}>+ Manual Invoice</button>
          <label className="btn btn-sm" style={{ background: '#065f46', color: 'white', cursor: 'pointer', margin: 0 }}>
            {ediUploading ? 'Processing...' : 'Upload EDI Response'}
            <input type="file" multiple onChange={handleEdiResponseUpload} style={{ display: 'none' }} disabled={ediUploading} />
          </label>
        </div>
      </div>

      {/* Tabs */}
      <div className="no-print" style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e2e8f0' }}>
        <button
          onClick={() => setActiveTab('invoices')}
          style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 14,
            borderBottom: activeTab === 'invoices' ? '2px solid #2563eb' : '2px solid transparent',
            color: activeTab === 'invoices' ? '#2563eb' : '#64748b',
            marginBottom: -2,
          }}
        >
          Invoices
        </button>
        <button
          onClick={() => setActiveTab('status')}
          style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 14,
            borderBottom: activeTab === 'status' ? '2px solid #2563eb' : '2px solid transparent',
            color: activeTab === 'status' ? '#2563eb' : '#64748b',
            marginBottom: -2,
          }}
        >
          Submission Status
        </button>
      </div>

      {activeTab === 'status' && (
        <SubmissionStatusTab
          submissionMonth={submissionMonth}
          setSubmissionMonth={setSubmissionMonth}
          submissionStatus={submissionStatus}
          loadingStatus={loadingStatus}
        />
      )}

      {ediUploadResults && (
        <div className="card no-print" style={{ marginBottom: 16, border: '1px solid #86efac', background: '#f0fdf4' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, color: '#065f46' }}>EDI Response Results</h3>
            <button className="btn btn-sm btn-secondary" onClick={() => setEdiUploadResults(null)}>Dismiss</button>
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <span style={{ fontSize: 13 }}>Total: <strong>{ediUploadResults.summary.total}</strong></span>
            <span style={{ fontSize: 13, color: '#16a34a' }}>Accepted: <strong>{ediUploadResults.summary.accepted}</strong></span>
            <span style={{ fontSize: 13, color: '#dc2626' }}>Rejected: <strong>{ediUploadResults.summary.rejected}</strong></span>
            <span style={{ fontSize: 13, color: '#2563eb' }}>Matched: <strong>{ediUploadResults.summary.matched}</strong></span>
          </div>
          {ediUploadResults.results.length > 0 && (
            <table style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>File</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Invoice #</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Details</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px' }}>Matched</th>
                </tr>
              </thead>
              <tbody>
                {ediUploadResults.results.map((r, i) => (
                  <tr key={i}>
                    <td style={{ padding: '4px 8px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.fileName}</td>
                    <td style={{ padding: '4px 8px' }}>{r.type}</td>
                    <td style={{ padding: '4px 8px', fontFamily: 'DM Mono, monospace' }}>{r.invoiceNumber || '—'}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                        background: r.status === 'accepted' ? '#d1fae5' : '#fee2e2',
                        color: r.status === 'accepted' ? '#065f46' : '#991b1b'
                      }}>{r.status}</span>
                    </td>
                    <td style={{ padding: '4px 8px', color: '#dc2626', fontSize: 12 }}>{r.errorMessage || ''}</td>
                    <td style={{ padding: '4px 8px' }}>{r.matched ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'invoices' && <>
      {/* Outstanding Balances Summary */}
      <div className="no-print" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
        <div className="card" style={{ marginBottom: 0 }}>
          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Total Outstanding</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: balances.total_outstanding > 0 ? '#dc2626' : '#16a34a', fontFamily: 'DM Mono, monospace' }}>
            {formatCurrency(balances.total_outstanding)}
          </div>
        </div>
        {balances.by_customer.slice(0, 3).map(cust => (
          <div key={cust.id} className="card" style={{ marginBottom: 0 }}>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>{cust.name}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: '#dc2626', fontFamily: 'DM Mono, monospace' }}>
              {formatCurrency(cust.outstanding)}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{cust.invoice_count} invoice{cust.invoice_count !== 1 ? 's' : ''}</div>
          </div>
        ))}
      </div>

      {/* Batch Invoice Generator */}
      <div className="card no-print" style={{ marginBottom: 16 }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setShowBatchGenerator(!showBatchGenerator)}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Batch Invoice Generator</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Find and invoice multiple projects at once</div>
          </div>
          <span style={{ fontSize: 18, color: '#64748b' }}>{showBatchGenerator ? '▼' : '▶'}</span>
        </div>

        {showBatchGenerator && (
          <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Period Start</label>
                <input
                  className="form-input"
                  type="date"
                  value={batchPeriod.start}
                  onChange={e => setBatchPeriod({ ...batchPeriod, start: e.target.value })}
                  style={{ width: 150 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Period End</label>
                <input
                  className="form-input"
                  type="date"
                  value={batchPeriod.end}
                  onChange={e => setBatchPeriod({ ...batchPeriod, end: e.target.value })}
                  style={{ width: 150 }}
                />
              </div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setBatchPeriod(getShiftedMonthDates(batchPeriod.start, -1))}
                style={{ alignSelf: 'flex-end' }}
              >
                Previous Month
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setBatchPeriod(getShiftedMonthDates(batchPeriod.start, 1))}
                style={{ alignSelf: 'flex-end' }}
              >
                Next Month
              </button>
              <button
                className="btn btn-secondary"
                onClick={findReadyProjects}
                disabled={findingProjects}
              >
                {findingProjects ? 'Searching...' : 'Find Projects'}
              </button>
            </div>

            {readyProjects.length > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    Found {readyProjects.length} project{readyProjects.length !== 1 ? 's' : ''} with approved timesheets
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary btn-sm" onClick={selectAllProjects}>Select All</button>
                    <button className="btn btn-secondary btn-sm" onClick={selectNoneProjects}>Select None</button>
                  </div>
                </div>

                <div className="table-wrap" style={{ marginBottom: 16 }}>
                  <table style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th>Project</th>
                        <th>Engineer(s)</th>
                        <th>Customer</th>
                        <th>Type</th>
                        <th>TS</th>
                        <th>Hours/Amt</th>
                        <th>Est. Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {readyProjects.map(proj => {
                        const overBudget = !!proj.over_budget;
                        return (
                        <tr key={proj.id} style={{ background: overBudget ? '#fffbeb' : undefined }}>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!selectedProjects[proj.id]}
                              onChange={() => toggleProjectSelection(proj.id)}
                              style={{ width: 16, height: 16 }}
                            />
                          </td>
                          <td title={proj.engineers?.join(', ')}>
                            <strong style={{ fontSize: 12 }}>{proj.project_name}</strong>
                            {proj.po_number && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>PO: {proj.po_number}</span>}
                            {proj.existing_invoice && (
                              <div style={{ fontSize: 10, color: '#1d4ed8', marginTop: 2 }}>
                                Note: Invoice #{proj.existing_invoice} exists for this period (additional uninvoiced entries found)
                              </div>
                            )}
                            {overBudget && (
                              <div style={{ fontSize: 10, color: '#d97706', marginTop: 2 }}>
                                Over budget — {formatCurrency(proj.remaining_balance)} remaining
                              </div>
                            )}
                          </td>
                          <td style={{ fontSize: 11, color: '#475569', maxWidth: 150 }} title={proj.engineers?.join(', ')}>
                            {proj.engineers?.join(', ') || '-'}
                          </td>
                          <td>{proj.customer_name}</td>
                          <td>
                            <span className={`badge ${proj.project_type === 'fixed_price' ? 'badge-fixed' : proj.project_type === 'fixed_monthly' ? 'badge-submitted' : 'badge-hourly'}`} style={{ fontSize: 10 }}>
                              {proj.project_type === 'fixed_price' ? 'Fixed' : proj.project_type === 'fixed_monthly' ? 'Monthly' : 'Hourly'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>{proj.timesheet_count}</td>
                          <td style={{ fontFamily: 'DM Mono, monospace' }}>
                            {proj.project_type === 'fixed_price'
                              ? formatCurrency(proj.fixed_amount)
                              : `${proj.total_hours.toFixed(2)} hrs`
                            }
                          </td>
                          <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, color: overBudget ? '#d97706' : '#10b981' }}>
                            {formatCurrency(proj.estimated_amount)}
                            {proj.remaining_balance !== null && (
                              <div style={{ fontSize: 9, color: '#64748b', fontWeight: 400 }}>
                                {formatCurrency(proj.remaining_balance)} left
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f8fafc', fontWeight: 600 }}>
                        <td colSpan="7" style={{ textAlign: 'right' }}>Selected Total:</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', color: '#10b981' }}>
                          {formatCurrency(
                            readyProjects
                              .filter(p => selectedProjects[p.id])
                              .reduce((sum, p) => sum + (p.estimated_amount || 0), 0)
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={generateBatchInvoices}
                  disabled={generatingBatch || Object.keys(selectedProjects).filter(id => selectedProjects[id]).length === 0}
                >
                  {generatingBatch
                    ? 'Generating...'
                    : `Generate ${Object.keys(selectedProjects).filter(id => selectedProjects[id]).length} Invoice(s)`
                  }
                </button>
              </>
            )}

            {readyProjects.length === 0 && !findingProjects && (
              <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>
                Click "Find Projects" to search for projects with approved timesheets in the selected date range.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Batch Payment */}
      <div className="card no-print" style={{ marginBottom: 16 }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setShowBatchPayment(!showBatchPayment)}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Batch Payment</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Record payment for multiple invoices at once</div>
          </div>
          <span style={{ fontSize: 18, color: '#64748b' }}>{showBatchPayment ? '▼' : '▶'}</span>
        </div>

        {showBatchPayment && (
          <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Customer</label>
                <select
                  className="form-select"
                  value={batchPayCustomer}
                  onChange={(e) => { setBatchPayCustomer(e.target.value); setBatchPaySelected({}); }}
                  style={{ minWidth: 250 }}
                >
                  <option value="">Select a customer...</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {batchPayCustomer && outstandingForCustomer.length > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    {outstandingForCustomer.length} outstanding invoice{outstandingForCustomer.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary btn-sm" onClick={selectAllBatchPay}>Select All</button>
                    <button className="btn btn-secondary btn-sm" onClick={selectNoneBatchPay}>Select None</button>
                  </div>
                </div>

                <div className="table-wrap" style={{ marginBottom: 16 }}>
                  <table style={{ fontSize: 12 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th>Invoice #</th>
                        <th>Project</th>
                        <th>Period</th>
                        <th>Total</th>
                        <th>Paid</th>
                        <th>Balance Due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outstandingForCustomer.map(inv => {
                        const balance = (inv.total_amount || 0) - (inv.amount_paid || 0);
                        return (
                          <tr key={inv.id} style={{ background: batchPaySelected[inv.id] ? '#eff6ff' : undefined }}>
                            <td>
                              <input
                                type="checkbox"
                                checked={!!batchPaySelected[inv.id]}
                                onChange={() => toggleBatchPayInvoice(inv.id)}
                                style={{ width: 16, height: 16 }}
                              />
                            </td>
                            <td style={{ fontFamily: 'DM Mono, monospace' }}>{inv.invoice_number}</td>
                            <td>{inv.project_name}</td>
                            <td style={{ fontSize: 11 }}>
                              {inv.period_start && inv.period_end
                                ? `${formatDate(inv.period_start)} - ${formatDate(inv.period_end)}`
                                : '—'}
                            </td>
                            <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(inv.total_amount)}</td>
                            <td style={{ fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(inv.amount_paid || 0)}</td>
                            <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, color: '#dc2626' }}>{formatCurrency(balance)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f8fafc', fontWeight: 600 }}>
                        <td colSpan="6" style={{ textAlign: 'right' }}>Selected Total:</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', color: '#1e40af', fontSize: 14 }}>
                          {formatCurrency(batchPayTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Payment details */}
                {Object.values(batchPaySelected).some(v => v) && (
                  <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Payment Details</div>
                    {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div>
                        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Payment Date *</label>
                        <input
                          className="form-input"
                          type="date"
                          value={batchPayForm.payment_date}
                          onChange={(e) => setBatchPayForm({ ...batchPayForm, payment_date: e.target.value })}
                          style={{ width: 160 }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Payment Method</label>
                        <select
                          className="form-select"
                          value={batchPayForm.payment_method}
                          onChange={(e) => setBatchPayForm({ ...batchPayForm, payment_method: e.target.value })}
                          style={{ width: 160 }}
                        >
                          <option value="">Select...</option>
                          {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Reference #</label>
                        <input
                          className="form-input"
                          value={batchPayForm.reference_number}
                          onChange={(e) => setBatchPayForm({ ...batchPayForm, reference_number: e.target.value })}
                          placeholder="Check #, transaction ID..."
                          style={{ width: 200 }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Notes</label>
                        <input
                          className="form-input"
                          value={batchPayForm.notes}
                          onChange={(e) => setBatchPayForm({ ...batchPayForm, notes: e.target.value })}
                          placeholder="Optional notes..."
                          style={{ width: 200 }}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
                      <button
                        className="btn btn-primary"
                        onClick={handleBatchPayment}
                        disabled={batchPaySaving}
                      >
                        {batchPaySaving
                          ? 'Recording...'
                          : `Record Payment for ${Object.values(batchPaySelected).filter(v => v).length} Invoice(s) — ${formatCurrency(batchPayTotal)}`
                        }
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {batchPayCustomer && outstandingForCustomer.length === 0 && (
              <div style={{ fontSize: 13, color: '#16a34a', fontStyle: 'italic' }}>
                No outstanding invoices for this customer.
              </div>
            )}

            {!batchPayCustomer && (
              <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>
                Select a customer to see their outstanding invoices.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payment Advice Import */}
      <div className="card no-print" style={{ marginBottom: 16 }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setShowAdviceImport(!showAdviceImport)}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Payment Advice Import</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Upload a payment advice (.xlsx) to match and clear invoices</div>
          </div>
          <span style={{ fontSize: 18, color: '#64748b' }}>{showAdviceImport ? '▼' : '▶'}</span>
        </div>

        {showAdviceImport && (
          <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                Choose File...
                <input type="file" accept=".xlsx,.xls" onChange={handleAdviceUpload} style={{ display: 'none' }} />
              </label>
              <span style={{ marginLeft: 12, fontSize: 13, color: '#64748b' }}>Supports .xlsx/.xls files (Mercedes and Stellantis/FCA payment advice)</span>
            </div>

            {adviceResults && (
              <div>
                {adviceResults.paymentInfo && (
                  <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{adviceResults.paymentInfo.source} Payment</div>
                    <div style={{ fontSize: 13, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                      <span>Doc #: <strong>{adviceResults.paymentInfo.document_number}</strong></span>
                      <span>Total: <strong>{formatCurrency(adviceResults.paymentInfo.total_amount)}</strong></span>
                      {adviceResults.paymentInfo.value_date && <span>Date: <strong>{adviceResults.paymentInfo.value_date}</strong></span>}
                    </div>
                  </div>
                )}
                {adviceResults.matches.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: '#16a34a' }}>
                      Matched Invoices ({adviceResults.matches.length})
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: 30 }}></th>
                            <th>Invoice #</th>
                            <th>Project</th>
                            <th>Invoice Amt</th>
                            <th>Advice Amt</th>
                            <th>Difference</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {adviceResults.matches.map(m => (
                            <tr key={m.invoice_id} style={{ opacity: m.already_paid ? 0.5 : 1 }}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={!!adviceSelected[m.invoice_id]}
                                  disabled={m.already_paid}
                                  onChange={(e) => setAdviceSelected({ ...adviceSelected, [m.invoice_id]: e.target.checked })}
                                />
                              </td>
                              <td><strong>{m.invoice_number}</strong></td>
                              <td>{m.project_name}<br /><span style={{ fontSize: 11, color: '#94a3b8' }}>{m.customer_name}</span></td>
                              <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(m.invoice_amount)}</td>
                              <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(m.advice_amount)}</td>
                              <td style={{ fontFamily: 'DM Mono, monospace' }}>
                                {(() => {
                                  const diff = m.advice_amount - m.balance_due;
                                  if (Math.abs(diff) < 0.01) return <span style={{ color: '#16a34a' }}>--</span>;
                                  return <span style={{ color: '#dc2626', fontWeight: 600 }} title={m.has_deductions ? 'Deduction applied by customer' : 'Partial payment'}>{formatCurrency(diff)}</span>;
                                })()}
                              </td>
                              <td>
                                {m.already_paid
                                  ? <span className="badge badge-paid">Already Paid</span>
                                  : m.is_partial
                                  ? <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>Partial: {formatCurrency(m.advice_amount)} of {formatCurrency(m.balance_due)}</span>
                                  : <span className="badge badge-unpaid">Balance: {formatCurrency(m.balance_due)}</span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ fontWeight: 700, borderTop: '2px solid #cbd5e1', background: '#f8fafc' }}>
                            <td></td>
                            <td colSpan={2} style={{ fontSize: 13 }}>
                              Selected Total ({adviceResults.matches.filter(m => adviceSelected[m.invoice_id]).length} invoices)
                            </td>
                            <td style={{ fontFamily: 'DM Mono, monospace' }}>
                              {formatCurrency(adviceResults.matches.filter(m => adviceSelected[m.invoice_id]).reduce((s, m) => s + m.invoice_amount, 0))}
                            </td>
                            <td style={{ fontFamily: 'DM Mono, monospace' }}>
                              {formatCurrency(adviceResults.matches.filter(m => adviceSelected[m.invoice_id]).reduce((s, m) => s + m.advice_amount, 0))}
                            </td>
                            <td style={{ fontFamily: 'DM Mono, monospace' }}>
                              {(() => {
                                const sel = adviceResults.matches.filter(m => adviceSelected[m.invoice_id]);
                                const diff = sel.reduce((s, m) => s + m.advice_amount, 0) - sel.reduce((s, m) => s + m.balance_due, 0);
                                return Math.abs(diff) < 0.01 ? '--' : <span style={{ color: '#dc2626' }}>{formatCurrency(diff)}</span>;
                              })()}
                            </td>
                            <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                              Balance: {formatCurrency(adviceResults.matches.filter(m => adviceSelected[m.invoice_id]).reduce((s, m) => s + m.balance_due, 0))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {adviceResults.unmatched.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, color: '#f59e0b' }}>
                      Unmatched ({adviceResults.unmatched.length})
                    </div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>Document #</th><th>Amount</th><th>Reference</th></tr></thead>
                        <tbody>
                          {adviceResults.unmatched.map((u, i) => (
                            <tr key={i}>
                              <td>{u.invoice_number}</td>
                              <td style={{ fontFamily: 'DM Mono, monospace' }}>{formatCurrency(u.amount)}</td>
                              <td style={{ fontSize: 12, color: '#64748b' }}>{u.reference || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleAdviceApply}
                    disabled={adviceProcessing || Object.values(adviceSelected).filter(v => v).length === 0}
                  >
                    {adviceProcessing
                      ? 'Processing...'
                      : `Mark ${Object.values(adviceSelected).filter(v => v).length} Invoice(s) as Paid`
                    }
                  </button>
                  <button className="btn btn-secondary" onClick={() => { setAdviceResults(null); setAdviceSelected({}); }}>
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filter and Sort Controls */}
      <div className="card no-print" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Projects:</span>
            <select
              className="form-select"
              value={projectStatusFilter}
              onChange={(e) => setProjectStatusFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 130 }}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Customer:</span>
            <select
              className="form-select"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 180 }}
            >
              <option value="">All Customers</option>
              {uniqueCustomers.map((c) => (
                <option key={c.name} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Engineer:</span>
            <select
              className="form-select"
              value={engineerFilter}
              onChange={(e) => setEngineerFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 150 }}
            >
              <option value="">All Engineers</option>
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Status:</span>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 150 }}
            >
              <option value="active">Active (Unpaid/Partial)</option>
              <option value="">All Invoices</option>
              <option value="unpaid">Unpaid Only</option>
              <option value="partial">Partial Only</option>
              <option value="paid">Paid Only</option>
              <option value="voided">Voided Only</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Year:</span>
            <select
              className="form-select"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 110 }}
              title="Unpaid and partial invoices from earlier years are always shown"
            >
              <option value="">All Years</option>
              {invoiceYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Received:</span>
            <select
              className="form-select"
              value={receivedFilter}
              onChange={(e) => setReceivedFilter(e.target.value)}
              style={{ width: 'auto', minWidth: 130 }}
            >
              <option value="">All</option>
              <option value="received">Received</option>
              <option value="not_received">Not Received</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>Sort by:</span>
            <select
              className="form-select"
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              style={{ width: 'auto', minWidth: 130 }}
            >
              <option value="created_at">Invoice Date</option>
              <option value="due_date">Due Date</option>
              <option value="invoice_number">Invoice #</option>
              <option value="customer_name">Customer</option>
              <option value="total_amount">Total Amount</option>
              <option value="balance">Balance Due</option>
            </select>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
              style={{ minWidth: 40 }}
            >
              {sortDir === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>
            Showing {sortedInvoices.length} of {invoices.length} invoices
          </span>
        </div>
      </div>

      <div className="card no-print">
        {sortedInvoices.length === 0 ? (
          <div className="empty-state">
            <h3>No invoices {statusFilter === 'active' ? 'with outstanding balance' : statusFilter ? `with status "${statusFilter}"` : 'yet'}</h3>
            <p>{statusFilter ? 'Try a different filter.' : 'Generate your first invoice from approved timesheets.'}</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <SortHeader field="invoice_number">Invoice #</SortHeader>
                  <SortHeader field="customer_name">Customer / Project</SortHeader>
                  <SortHeader field="created_at">Date</SortHeader>
                  <SortHeader field="due_date">Due</SortHeader>
                  <th>Hours</th>
                  <SortHeader field="total_amount">Total</SortHeader>
                  <th>Paid</th>
                  <SortHeader field="balance">Balance</SortHeader>
                  <th>Status</th>
                  <th>EDI</th>
                  <th style={{ width: 320 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedInvoices.map((inv) => {
                  const balance = (inv.total_amount || 0) - (inv.amount_paid || 0);
                  const invoiceDate = inv.created_at ? formatDate(inv.created_at) : '—';
                  return (
                    <tr key={inv.id} style={inv.status === 'voided' ? { opacity: 0.6 } : {}}>
                      <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13 }}>{inv.invoice_number}</td>
                      <td title={inv.engineers || ''}>
                        <strong>{inv.customer_name}</strong>
                        <br />
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>{inv.project_name}</span>
                        {inv.engineers && (
                          <>
                            <br />
                            <span style={{ fontSize: 11, color: '#64748b' }}>{inv.engineers}</span>
                          </>
                        )}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {invoiceDate}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {(() => {
                          if (!inv.created_at) return '—';
                          const days = parseInt((inv.payment_terms || 'Net 30').replace(/\D/g, '')) || 30;
                          if (days === 0) return invoiceDate;
                          const due = new Date(new Date(inv.created_at).getTime() + days * 86400000);
                          const isOverdue = inv.status !== 'paid' && inv.status !== 'voided' && due < new Date();
                          return <span style={isOverdue ? { color: '#dc2626', fontWeight: 600 } : {}}>{formatDate(due.toISOString())}</span>;
                        })()}
                      </td>
                      <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, color: '#64748b' }}>
                        {inv.total_hours > 0 ? inv.total_hours.toFixed(2) : '—'}
                      </td>
                      <td style={{ fontFamily: 'DM Mono, monospace' }}>
                        {formatCurrency(inv.total_amount)}
                      </td>
                      <td style={{ fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>
                        {formatCurrency(inv.amount_paid || 0)}
                      </td>
                      <td style={{ fontFamily: 'DM Mono, monospace', fontWeight: 600, color: balance > 0 ? '#dc2626' : '#16a34a' }}>
                        {formatCurrency(balance)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span title={inv.void_reason || undefined}>{getStatusBadge(getStatus(inv))}</span>
                          {inv.void_reason && inv.status === 'voided' && (
                            <span style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic', width: '100%' }}>{inv.void_reason}</span>
                          )}
                          {inv.emailed_at && (
                            <span title={`Emailed ${new Date(inv.emailed_at).toLocaleDateString()}`} style={{ color: '#16a34a', fontSize: 13 }}>✉</span>
                          )}
                          {inv.received_at && (
                            <span title={`Received ${new Date(inv.received_at).toLocaleDateString()}`} style={{ color: '#8b5cf6', fontSize: 13 }}>✓</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {inv.edi_status === 'accepted' && (
                          <span title={inv.edi_response_date ? `Accepted ${formatDate(inv.edi_response_date)}` : 'Accepted'} style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                            background: '#d1fae5', color: '#065f46'
                          }}>OK</span>
                        )}
                        {inv.edi_status === 'rejected' && (
                          <span title={inv.edi_error_details || 'Rejected'} style={{
                            padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                            background: '#fee2e2', color: '#991b1b', cursor: 'help'
                          }}>REJ</span>
                        )}
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => viewInvoice(inv)} style={{ marginRight: 4 }}>View</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEmail(inv)} disabled={emailingId === inv.id} style={{ marginRight: 4 }}>{emailingId === inv.id ? 'Sending...' : 'Email'}</button>
                        {!!inv.edi_invoicing && inv.po_number && (
                          <button className="btn btn-sm" onClick={() => handleDownloadEdi810(inv)} style={{ marginRight: 4, background: '#065f46', color: 'white' }}>EDI 810</button>
                        )}
                        <button
                          className={`btn btn-sm ${inv.received_at ? 'btn-success' : 'btn-secondary'}`}
                          onClick={() => handleReceived(inv)}
                          style={{ marginRight: 4 }}
                          title={inv.received_at ? `Received ${new Date(inv.received_at).toLocaleDateString()}` : 'Mark as received'}
                        >
                          {inv.received_at ? 'Rcvd ✓' : 'Rcvd'}
                        </button>
                        {getStatus(inv) !== 'voided' && getStatus(inv) !== 'paid' && (
                          <button className="btn btn-primary btn-sm" onClick={() => openPayment(inv)} style={{ marginRight: 4 }}>Paid</button>
                        )}
                        {getStatus(inv) === 'voided' ? (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleUnvoid(inv)} style={{ marginRight: 4 }}>Restore</button>
                        ) : (
                          <button className="btn btn-danger btn-sm" onClick={() => handleVoid(inv)} style={{ marginRight: 4 }}>Void</button>
                        )}
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(inv.id, inv.invoice_number)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>}

      {modal === 'generate' && (
        <Modal
          title="Generate Invoice"
          onClose={() => setModal(null)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleGenerate} disabled={saving}>
                {saving ? 'Generating...' : 'Generate Invoice'}
              </button>
            </>
          }
        >
          <form onSubmit={handleGenerate}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="alert alert-info">
              This will generate an invoice from all approved timesheets for the selected project within the date range.
            </div>
            <div className="form-group">
              <label className="form-label">Project *</label>
              <select
                className="form-select"
                value={generateForm.project_id}
                onChange={(e) => setGenerateForm({ ...generateForm, project_id: e.target.value })}
              >
                <option value="">Select a project...</option>
                {projects.filter(p => !p.internal).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.customer_name})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Period Start *</label>
                <input
                  className="form-input"
                  type="date"
                  value={generateForm.period_start}
                  onChange={(e) => setGenerateForm({ ...generateForm, period_start: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Period End *</label>
                <input
                  className="form-input"
                  type="date"
                  value={generateForm.period_end}
                  onChange={(e) => setGenerateForm({ ...generateForm, period_end: e.target.value })}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setGenerateForm({ ...generateForm, ...getPreviousMonthDates() })}
              >
                Previous Month
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setGenerateForm({ ...generateForm, ...getCurrentMonthDates() })}
              >
                Current Month
              </button>
            </div>
            <PrintNotesField
              value={generateForm.print_notes}
              onChange={(value) => setGenerateForm({ ...generateForm, print_notes: value })}
            />
          </form>
        </Modal>
      )}

      {modal === 'manual' && (
        <Modal
          title="Manual Invoice"
          onClose={() => setModal(null)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleManualInvoice} disabled={saving}>
                {saving ? 'Creating...' : 'Create Invoice'}
              </button>
            </>
          }
        >
          <form onSubmit={handleManualInvoice}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="alert alert-info">
              Create an invoice without timesheets — for fixed-price work, vendor pass-through, or any ad-hoc billing.
            </div>
            <div className="form-group">
              <label className="form-label">Project *</label>
              <select
                className="form-select"
                value={manualForm.project_id}
                onChange={(e) => setManualForm({ ...manualForm, project_id: e.target.value })}
              >
                <option value="">Select a project...</option>
                {projects.filter(p => !p.internal).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.customer_name})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Amount *</label>
              <input
                className="form-input"
                type="number"
                step="0.01"
                min="0"
                value={manualForm.total_amount}
                onChange={(e) => setManualForm({ ...manualForm, total_amount: e.target.value })}
                placeholder="Invoice amount"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description *</label>
              <input
                className="form-input"
                type="text"
                value={manualForm.description}
                onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })}
                placeholder="e.g. On-site training per vendor quote Q-12345"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Period Start</label>
                <input
                  className="form-input"
                  type="date"
                  value={manualForm.period_start}
                  onChange={(e) => setManualForm({ ...manualForm, period_start: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Period End</label>
                <input
                  className="form-input"
                  type="date"
                  value={manualForm.period_end}
                  onChange={(e) => setManualForm({ ...manualForm, period_end: e.target.value })}
                />
              </div>
            </div>
            <PrintNotesField
              value={manualForm.print_notes}
              onChange={(value) => setManualForm({ ...manualForm, print_notes: value })}
            />
            <div className="form-group">
              <label className="form-label">Internal notes (optional, not printed)</label>
              <textarea
                className="form-textarea"
                value={manualForm.notes}
                onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                placeholder="For your records only"
                rows={2}
              />
            </div>
          </form>
        </Modal>
      )}

      {modal === 'view' && inv && (
        <>
          {/* Invoice View Modal (non-print) */}
          <div className="modal-overlay no-print" onClick={() => setModal(null)}>
            <div className="modal" style={{ maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">Invoice #{inv.invoice_number}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" onClick={handlePrint}>Print</button>
                  <button className="btn btn-secondary" onClick={handleRegenerate} disabled={saving}>
                    {saving ? 'Regenerating...' : 'Regenerate'}
                  </button>
                  <button className="btn btn-danger" onClick={() => handleDelete(inv.id, inv.invoice_number)}>Delete</button>
                  <button className="btn btn-secondary" onClick={() => setModal(null)}>Close</button>
                </div>
              </div>

              {/* Invoice Preview */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 24, background: 'white' }}>
                <InvoiceContent inv={inv} settings={settings} />
              </div>

              {inv.status !== 'voided' && inv.id && (
                <InvoiceNotesEditor
                  invoice={inv}
                  onSaved={(printNotes) => setViewingInvoice(prev => ({ ...prev, print_notes: printNotes }))}
                />
              )}

              {/* Invoice Adjustments */}
              {inv.status !== 'voided' && (
                <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border)', borderRadius: 8, background: '#fefce8' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Invoice Adjustments</div>
                  <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                    Add line items for prior period adjustments, deductions, or credits. These appear on the invoice but do not affect project budget or engineer pay.
                  </p>
                  {inv.adjustments && inv.adjustments.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#fef3c7' }}>
                          <th style={{ border: '1px solid #e5e7eb', padding: 6, textAlign: 'left' }}>Description</th>
                          <th style={{ border: '1px solid #e5e7eb', padding: 6, textAlign: 'right', width: 80 }}>Hours</th>
                          <th style={{ border: '1px solid #e5e7eb', padding: 6, textAlign: 'right', width: 80 }}>Rate</th>
                          <th style={{ border: '1px solid #e5e7eb', padding: 6, textAlign: 'right', width: 100 }}>Amount</th>
                          <th style={{ border: '1px solid #e5e7eb', padding: 6, width: 60 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {inv.adjustments.map(adj => (
                          <tr key={adj.id}>
                            <td style={{ border: '1px solid #e5e7eb', padding: 6 }}>{adj.description}</td>
                            <td style={{ border: '1px solid #e5e7eb', padding: 6, textAlign: 'right' }}>{adj.hours || '—'}</td>
                            <td style={{ border: '1px solid #e5e7eb', padding: 6, textAlign: 'right' }}>{adj.rate ? `$${adj.rate.toFixed(2)}` : '—'}</td>
                            <td style={{ border: '1px solid #e5e7eb', padding: 6, textAlign: 'right', fontWeight: 600 }}>${adj.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                            <td style={{ border: '1px solid #e5e7eb', padding: 6, textAlign: 'center' }}>
                              <button className="btn btn-danger btn-sm" onClick={async () => {
                                if (!window.confirm('Remove this adjustment?')) return;
                                try {
                                  await apiFetch(`/invoices/${inv.id}/adjustments/${adj.id}`, { method: 'DELETE' });
                                  viewInvoice(inv);
                                  await loadData();
                                } catch (err) { alert(err.message); }
                              }}>x</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Description</label>
                      <input className="form-input" id="adj-desc" placeholder="Prior period adjustment - Inv #..." style={{ width: 280, fontSize: 13 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Hours</label>
                      <input className="form-input" id="adj-hours" type="number" step="0.01" placeholder="0" style={{ width: 70, fontSize: 13 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Rate</label>
                      <input className="form-input" id="adj-rate" type="number" step="0.01" placeholder="0.00" style={{ width: 80, fontSize: 13 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Amount</label>
                      <input className="form-input" id="adj-amount" type="number" step="0.01" placeholder="0.00" style={{ width: 100, fontSize: 13 }} />
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={async () => {
                      const desc = document.getElementById('adj-desc').value;
                      const hours = parseFloat(document.getElementById('adj-hours').value) || 0;
                      const rate = parseFloat(document.getElementById('adj-rate').value) || 0;
                      let amount = parseFloat(document.getElementById('adj-amount').value) || 0;
                      if (!amount && hours && rate) amount = hours * rate;
                      if (!desc.trim() || !amount) { alert('Description and amount are required'); return; }
                      try {
                        await apiFetch(`/invoices/${inv.id}/adjustments`, { method: 'POST', body: { description: desc, hours, rate, amount } });
                        document.getElementById('adj-desc').value = '';
                        document.getElementById('adj-hours').value = '';
                        document.getElementById('adj-rate').value = '';
                        document.getElementById('adj-amount').value = '';
                        viewInvoice(inv);
                        await loadData();
                      } catch (err) { alert(err.message); }
                    }}>Add</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Print-only Invoice + Timesheets */}
          <div className="print-only" style={{ display: 'none' }}>
            <style>
              {`
                .print-only { display: none; }
                @media print {
                  .print-only { display: block !important; }
                  .no-print, .sidebar, .mobile-header, .mobile-top-header, .mobile-nav, .page-header { display: none !important; }
                  body { background: white !important; padding: 0 !important; margin: 0 !important; font-size: 8pt !important; }
                  .main-content { margin: 0 !important; padding: 0 !important; }
                  .card { display: none !important; }
                  .app-shell { display: block !important; }
                  .print-invoice { padding: 8px; font-size: 10px; }
                  .print-invoice table { font-size: 9px; }
                  .timesheet-page { page-break-before: always !important; break-before: page !important; page-break-inside: avoid; padding: 10px; }
                  .daily-time-report { font-family: Arial, sans-serif; font-size: 6pt; padding: 0; }
                  .daily-time-report table { border-collapse: collapse; width: 100%; }
                  .daily-time-report th, .daily-time-report td { border: 1px solid #000; padding: 1px 2px; font-size: 6pt; }
                  .daily-time-report th { background: #f5f5f5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                }
                @page { margin: 0.25in; size: letter; }
              `}
            </style>
            <div className="print-invoice">
              <InvoiceContent inv={inv} settings={settings} />
            </div>
            {/* Print timesheets after invoice (only if include_timesheets is enabled) */}
            {inv.include_timesheets && inv.timesheetDetails && inv.timesheetDetails.map((ts, idx) => (
              <div key={ts.id} className="timesheet-page">
                <DailyTimeReport
                  timesheet={ts}
                  settings={settings}
                  projectName={inv.project_name || inv.project?.name}
                  customerName={inv.customer_name || inv.project?.customer_name}
                  location={inv.location || inv.project?.location}
                  poNumber={inv.po_number || inv.project?.po_number}
                  periodStart={inv.period_start}
                  periodEnd={inv.period_end}
                  currencySymbol={inv.currency_symbol || inv.project?.currency_symbol || '$'}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {modal === 'payment' && viewingInvoice && (
        <Modal
          title={`Record Payment - Invoice #${viewingInvoice.invoice_number}`}
          onClose={() => setModal(null)}
          footer={<button className="btn btn-secondary" onClick={() => setModal(null)}>Close</button>}
        >
          {error && <div className="alert alert-error">{error}</div>}

          {/* Invoice Summary */}
          <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8, marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, textAlign: 'center' }}>
              <div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Total</div>
                {editingAmount !== null ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                    <input
                      type="number"
                      step="0.01"
                      value={editingAmount}
                      onChange={(e) => setEditingAmount(e.target.value)}
                      style={{ width: 110, fontFamily: 'DM Mono, monospace', fontSize: 14, padding: '4px 8px', textAlign: 'right' }}
                      className="form-input"
                    />
                    <button className="btn btn-primary btn-sm" onClick={async () => {
                      try {
                        await apiFetch(`/invoices/${viewingInvoice.id}`, { method: 'PUT', body: { total_amount: parseFloat(editingAmount) } });
                        setViewingInvoice({ ...viewingInvoice, total_amount: parseFloat(editingAmount), amount_paid: viewingInvoice.status === 'paid' ? parseFloat(editingAmount) : viewingInvoice.amount_paid });
                        setEditingAmount(null);
                        await loadData();
                      } catch (e) { alert('Error: ' + e.message); }
                    }}>Save</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditingAmount(null)}>X</button>
                  </div>
                ) : (
                  <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'DM Mono, monospace', cursor: 'pointer' }} onClick={() => setEditingAmount(viewingInvoice.total_amount)} title="Click to edit">
                    {formatCurrency(viewingInvoice.total_amount)}
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Paid</div>
                <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>
                  {formatCurrency(viewingInvoice.amount_paid || 0)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Balance Due</div>
                <div style={{ fontSize: 18, fontWeight: 600, fontFamily: 'DM Mono, monospace', color: '#dc2626' }}>
                  {formatCurrency((viewingInvoice.total_amount || 0) - (viewingInvoice.amount_paid || 0))}
                </div>
              </div>
            </div>
          </div>

          {/* Payment History */}
          {payments.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Payment History</div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(pmt => (
                      <tr key={pmt.id}>
                        <td>{formatDate(pmt.payment_date)}</td>
                        <td style={{ fontFamily: 'DM Mono, monospace', color: '#16a34a' }}>{formatCurrency(pmt.amount)}</td>
                        <td>{pmt.payment_method || '—'}</td>
                        <td style={{ fontSize: 12 }}>{pmt.reference_number || '—'}</td>
                        <td>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeletePayment(pmt.id)}>Del</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Record New Payment */}
          {(viewingInvoice.total_amount || 0) - (viewingInvoice.amount_paid || 0) > 0 && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Record New Payment</div>
              <form onSubmit={handleRecordPayment}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Amount *</label>
                    <input
                      className="form-input"
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Date *</label>
                    <input
                      className="form-input"
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Payment Method</label>
                    <select
                      className="form-select"
                      value={paymentForm.payment_method}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                    >
                      <option value="">Select...</option>
                      {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reference # (Check #, etc.)</label>
                    <input
                      className="form-input"
                      value={paymentForm.reference_number}
                      onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                      placeholder="Check number, transaction ID..."
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input
                    className="form-input"
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder="Optional notes..."
                  />
                </div>
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? 'Recording...' : 'Record Payment'}
                </button>
              </form>
            </div>
          )}

          {(viewingInvoice.total_amount || 0) - (viewingInvoice.amount_paid || 0) <= 0 && (
            <div className="alert alert-success">
              This invoice has been paid in full.
            </div>
          )}
        </Modal>
      )}

    </div>
  );
}

function DailyTimeReport({ timesheet, settings, projectName, customerName, location, poNumber, periodStart, periodEnd, currencySymbol = '$' }) {
  const ts = timesheet;
  const weekEnding = ts.week_ending
    ? new Date(ts.week_ending + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
    : '';

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Create a map of entries by date
  const entriesByDate = {};
  if (ts.entries) {
    ts.entries.forEach(e => {
      const dateKey = e.entry_date ? e.entry_date.split('T')[0] : '';
      if (dateKey) entriesByDate[dateKey] = e;
    });
  }

  // Calculate week dates (Monday through Sunday)
  const getWeekDates = () => {
    if (!ts.week_ending) return [];
    const weekEnd = new Date(ts.week_ending + 'T00:00:00');
    const dates = [];
    for (let i = -6; i <= 0; i++) {
      const d = new Date(weekEnd);
      d.setDate(weekEnd.getDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  };

  const allWeekDates = getWeekDates();

  // Filter to only dates within the invoice period
  const weekDates = (periodStart && periodEnd) ? allWeekDates.filter(date => date >= periodStart && date <= periodEnd) : allWeekDates;

  // Calculate totals
  let totalST = 0, totalOT = 0, totalPT = 0;
  weekDates.forEach(date => {
    const entry = entriesByDate[date];
    if (entry && entry.hours) {
      totalST += entry.hours; // All hours as ST for now
    }
  });
  const grandTotal = totalST + totalOT + totalPT;
  const isFixedMonthly = ts.is_fixed_monthly;
  const rate = isFixedMonthly ? 0 : (ts.bill_rate || 0);
  const laborSubtotal = isFixedMonthly ? (ts.bill_rate || 0) : (grandTotal * rate);

  // Styles - ultra compact to fit on one page even on mobile
  const cellStyle = { border: '1px solid #000', padding: '1px 2px', fontSize: '6pt', height: '14px', verticalAlign: 'middle' };
  const headerCell = { ...cellStyle, fontWeight: 'bold', background: '#f5f5f5', textAlign: 'center', height: '12px' };
  const centerCell = { ...cellStyle, textAlign: 'center' };
  const rightCell = { ...cellStyle, textAlign: 'right' };
  const descRowStyle = { border: '1px solid #000', padding: '1px 2px', fontSize: '6pt', height: '45px', verticalAlign: 'top' };

  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${m} ${ampm}`;
  };

  return (
    <div className="daily-time-report" style={{ fontFamily: 'Arial, sans-serif', fontSize: '6pt', padding: 0, width: '100%' }}>
      {/* Header Section - ultra compact */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1px', width: '100%' }}>
        {/* Left: Logo and Company Info */}
        <div style={{ flex: '0 0 auto', width: '160px', paddingRight: '5px' }}>
          {settings?.company_logo && (
            <img src={settings.company_logo} alt="Logo" style={{ maxWidth: '80px', maxHeight: '30px', marginBottom: '1px', display: 'block' }} />
          )}
          <div style={{ fontWeight: 'bold', fontStyle: 'italic', fontSize: '7pt' }}>
            {settings?.company_name || 'Company Name'}
          </div>
          <div style={{ fontSize: '5pt' }}>Service at: <strong>{customerName}</strong></div>
          <div style={{ fontSize: '5pt' }}>Location: {location || ''}</div>
        </div>
        {/* Center: Title and Rate Info */}
        <div style={{ flex: '0 0 auto', width: '160px', textAlign: 'center', padding: '0 5px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '9pt', marginBottom: '1px' }}>Daily Time Report</div>
          <div style={{ fontSize: '5pt', lineHeight: '1.2' }}>
            Mon shift 1 - Sun shift 3<br/>
            {isFixedMonthly ? 'Fixed Monthly' : `${currencySymbol}${rate.toFixed(2)}/hr`} | ST = All | OT/PT = N/A
          </div>
        </div>
        {/* Right: Timesheet Info - compact */}
        <div style={{ flex: '0 0 auto', width: '180px', fontSize: '5pt', lineHeight: '1.1' }}>
          <div><span style={{ display: 'inline-block', width: '55px', textAlign: 'right', paddingRight: '2px' }}>Week Ending:</span><strong>{weekEnding}</strong></div>
          <div><span style={{ display: 'inline-block', width: '55px', textAlign: 'right', paddingRight: '2px' }}>Engineer:</span>{ts.engineer_name}</div>
          <div><span style={{ display: 'inline-block', width: '55px', textAlign: 'right', paddingRight: '2px' }}>Engineer ID:</span>{ts.engineer_id || ''}</div>
          <div><span style={{ display: 'inline-block', width: '55px', textAlign: 'right', paddingRight: '2px' }}>Work Order #:</span>{poNumber || ''}</div>
          <div><span style={{ display: 'inline-block', width: '55px', textAlign: 'right', paddingRight: '2px' }}>Project:</span>{projectName}</div>
        </div>
      </div>

      {/* Daily Entries Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '4px' }}>
        <thead>
          <tr>
            <th style={{ ...headerCell, width: '55px' }}>Date</th>
            <th style={{ ...headerCell, width: '50px' }}>Travel To</th>
            <th style={{ ...headerCell, width: '55px' }}>Travel From</th>
            <th style={{ ...headerCell, width: '30px' }}>Shift</th>
            <th style={{ ...headerCell, width: '40px' }}>On Call</th>
            <th style={{ ...headerCell, width: '50px' }}>Start Time</th>
            <th style={{ ...headerCell, width: '50px' }}>End Time</th>
            <th style={{ ...headerCell, width: '28px' }}>ST</th>
            <th style={{ ...headerCell, width: '28px' }}>OT</th>
            <th style={{ ...headerCell, width: '28px' }}>PT</th>
            <th style={{ ...headerCell, width: '28px' }}>STT</th>
            <th style={{ ...headerCell, width: '28px' }}>OTT</th>
            <th style={{ ...headerCell, width: '35px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {weekDates.map((date) => {
            const entry = entriesByDate[date] || {};
            const dateObj = new Date(date + 'T00:00:00');
            const formattedDate = `${dateObj.getMonth() + 1}/${dateObj.getDate()}/${dateObj.getFullYear()}`;
            const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dateObj.getDay()];
            const hours = entry.hours || 0;
            const st = hours > 0 ? hours.toFixed(2) : '0.00';

            return (
              <React.Fragment key={date}>
                {/* Time Row */}
                <tr>
                  <td style={{ ...centerCell, whiteSpace: 'nowrap' }}>{formattedDate} {dayName}</td>
                  <td style={centerCell}>{location || ''}</td>
                  <td style={centerCell}></td>
                  <td style={centerCell}>{entry.shift || '1'}</td>
                  <td style={centerCell}></td>
                  <td style={centerCell}>{formatTime(entry.start_time)}</td>
                  <td style={centerCell}>{formatTime(entry.end_time)}</td>
                  <td style={centerCell}>{hours > 0 ? st : '0.00'}</td>
                  <td style={centerCell}>0.00</td>
                  <td style={centerCell}>0.00</td>
                  <td style={centerCell}>0.00</td>
                  <td style={centerCell}>0.00</td>
                  <td style={{ ...centerCell, fontWeight: 'bold' }}>{hours > 0 ? st : '0.00'}</td>
                </tr>
                {/* Description Row */}
                <tr>
                  <td style={descRowStyle}><strong>Detailed Description of Work:</strong></td>
                  <td colSpan={12} style={descRowStyle}>{entry.description || ''}</td>
                </tr>
              </React.Fragment>
            );
          })}
          {/* Weekly Totals Row */}
          <tr style={{ background: '#f5f5f5' }}>
            <td colSpan={7} style={{ ...cellStyle, fontWeight: 'bold' }}>Weekly Totals:</td>
            <td style={{ ...centerCell, fontWeight: 'bold' }}>{totalST.toFixed(2)}</td>
            <td style={{ ...centerCell, fontWeight: 'bold' }}>{totalOT.toFixed(2)}</td>
            <td style={{ ...centerCell, fontWeight: 'bold' }}>{totalPT.toFixed(2)}</td>
            <td style={{ ...centerCell, fontWeight: 'bold' }}>0.00</td>
            <td style={{ ...centerCell, fontWeight: 'bold' }}>0.00</td>
            <td style={{ ...centerCell, fontWeight: 'bold' }}>{grandTotal.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      {/* Bottom Section: Signatures and Pay Totals - ultra compact */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '5pt', lineHeight: '1.1', marginTop: '2px' }}>
        {/* Left: Signatures */}
        <div style={{ width: '48%', paddingRight: '5px' }}>
          <div style={{ marginBottom: '2px' }}>
            <div style={{ borderBottom: '1px solid #000', height: '10px', marginBottom: '1px' }}></div>
            <div>Certified by: <span style={{ marginLeft: '20px' }}>Date: _______</span></div>
            <div style={{ fontSize: '4pt' }}>{settings?.company_name || 'Company'} Site Lead</div>
          </div>
          <div>
            <div style={{ borderBottom: '1px solid #000', height: '10px', marginBottom: '1px' }}></div>
            <div>Approved by: <span style={{ marginLeft: '20px' }}>Date: _______</span></div>
            <div style={{ fontSize: '4pt' }}>Customer Representative</div>
          </div>
        </div>
        {/* Right: Expenses/Pay Summary - compact */}
        <div style={{ width: '50%', border: '1px solid #000' }}>
          <div style={{ background: '#f5f5f5', textAlign: 'center', fontWeight: 'bold', borderBottom: '1px solid #000', padding: '0px 1px', fontSize: '5pt' }}>Expenses</div>
          <div style={{ display: 'flex', padding: '0px 2px', fontSize: '5pt' }}>
            <span>Air: {currencySymbol}0 | Car: {currencySymbol}0 | Meals: {currencySymbol}0 | Parking: {currencySymbol}0 | Misc: {currencySymbol}0</span>
          </div>
          <div style={{ textAlign: 'right', padding: '0px 2px', fontSize: '5pt' }}><strong>Exp Subtotal:</strong> {currencySymbol}0.00</div>
          <div style={{ textAlign: 'right', padding: '0px 2px', fontSize: '5pt' }}>
            {isFixedMonthly ? `Fixed Monthly | Hours: ${grandTotal.toFixed(2)}` : `Rate: ${currencySymbol}${rate.toFixed(2)}/hr | Hours: ${grandTotal.toFixed(2)}`}
          </div>
          <div style={{ textAlign: 'right', padding: '1px 2px', fontWeight: 'bold', fontSize: '6pt' }}>Total: {currencySymbol}{laborSubtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
        </div>
      </div>
    </div>
  );
}

function InvoiceContent({ inv, settings }) {
  // Handle both generated (nested in project) and loaded (flat) invoice data structures
  const customerName = inv.customer_name || inv.project?.customer_name || '';
  const customerAddress = inv.customer_address || inv.project?.customer_address || '';
  const supplierNumber = inv.supplier_number || inv.project?.supplier_number || '';
  const paymentTerms = inv.payment_terms || inv.project?.payment_terms || 'Net 30';
  const poNumber = inv.po_number || inv.project?.po_number || '';
  const projectName = inv.project_name || inv.project?.name || '';
  const projectDescription = inv.project_description || inv.project?.project_description || inv.project?.description || '';
  const contactName = inv.contact_name || inv.project?.contact_name || '';
  const cs = inv.currency_symbol || inv.project?.currency_symbol || '$';
  const periodRange = `${formatDate(inv.period_start)} to ${formatDate(inv.period_end)}`;
  const showPeriod = (inv.show_invoice_period ?? inv.project?.show_invoice_period) !== 0;
  const withPeriod = (text, range) => (showPeriod ? `${text} - ${range}` : text);

  const weekRange = (weekEnding) => {
    if (!weekEnding) return periodRange;
    const end = new Date(weekEnding.split('T')[0] + 'T00:00:00');
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    return `${formatDate(start.toISOString().split('T')[0])} to ${formatDate(end.toISOString().split('T')[0])}`;
  };

  const invoiceDate = inv.created_at ? formatDate(inv.created_at) : formatDate(new Date().toISOString().split('T')[0]);
  const dueDate = getDueDate(inv.created_at || new Date(), paymentTerms);

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', color: '#000' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        {/* Company Info */}
        <div style={{ lineHeight: 1.4 }}>
          <div style={{ fontWeight: 'bold', fontSize: 16 }}>{settings.company_name || 'Your Company Name'}</div>
          {settings.company_address && <div>{settings.company_address}</div>}
          {settings.company_city_state_zip && <div>{settings.company_city_state_zip}</div>}
          {settings.company_phone && <div>Phone: {settings.company_phone}</div>}
          {settings.company_fax && <div>Fax: {settings.company_fax}</div>}
          {settings.company_email && <div>E-mail: {settings.company_email}</div>}
        </div>

        {/* Invoice Details */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 8 }}>Invoice</div>
          <table style={{ marginLeft: 'auto', borderCollapse: 'collapse', fontSize: 12 }}>
            <tbody>
              <tr>
                <td style={{ padding: '2px 8px', textAlign: 'left' }}>Invoice no:</td>
                <td style={{ padding: '2px 8px', textAlign: 'right', fontWeight: 'bold' }}>{inv.invoice_number}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 8px', textAlign: 'left' }}>Invoice date:</td>
                <td style={{ padding: '2px 8px', textAlign: 'right' }}>{invoiceDate}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 8px', textAlign: 'left' }}>Due date:</td>
                <td style={{ padding: '2px 8px', textAlign: 'right' }}>{dueDate}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 8px', textAlign: 'left' }}>Supplier ID:</td>
                <td style={{ padding: '2px 8px', textAlign: 'right' }}>{supplierNumber || '—'}</td>
              </tr>
              <tr>
                <td style={{ padding: '2px 8px', textAlign: 'left' }}>PO Number:</td>
                <td style={{ padding: '2px 8px', textAlign: 'right', fontWeight: 'bold' }}>{poNumber || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Bill To and Logo */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>To:</div>
          <div style={{ lineHeight: 1.5, marginLeft: 20 }}>
            <div style={{ fontWeight: 'bold' }}>{customerName}</div>
            {customerAddress && customerAddress.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        </div>
        {settings.company_logo && (
          <div style={{ textAlign: 'right' }}>
            <img
              src={settings.company_logo}
              alt="Company Logo"
              style={{ maxWidth: 200, maxHeight: 80, objectFit: 'contain' }}
            />
          </div>
        )}
      </div>

      {/* Project Info */}
      <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
        <strong>Project:</strong> {projectName}
      </div>

      {/* Sales Info */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, fontSize: 12, borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc', padding: '8px 0' }}>
        <div><strong>Sales Person:</strong> {settings.company_name || '—'}</div>
        <div><strong>Contact name:</strong> {contactName || '—'}</div>
        <div><strong>Payment terms:</strong> {paymentTerms}</div>
      </div>

      {/* Line Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
        <thead>
          <tr style={{ background: '#f0f0f0' }}>
            <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Qty.</th>
            <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Item</th>
            <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Description</th>
            <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Unit Price</th>
            <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Discount</th>
            <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Line Total</th>
          </tr>
        </thead>
        <tbody>
          {inv.lineItems && inv.lineItems.length > 0 ? (
            <>
            {inv.lineItems.map((item, idx) => (
              <tr key={idx} style={item.is_adjustment ? { background: '#fffbeb' } : undefined}>
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                  {item.is_adjustment
                    ? (item.hours ? item.hours.toFixed(2) : '1')
                    : item.is_consolidated
                    ? (item.hours ? item.hours.toFixed(2) : '1')
                    : item.is_fixed_price ? (inv.billing_method === 'monthly_installment' ? 'Monthly' : `${item.percentage}%`) : item.is_fixed_monthly ? `${item.hours?.toFixed(2)} hrs` : item.hours?.toFixed(2)}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>{poNumber || 'Engineering'}</td>
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                  {item.is_adjustment
                    ? item.description
                    : item.is_consolidated
                    ? withPeriod(projectDescription || 'Engineering Services', periodRange)
                    : item.is_fixed_price
                    ? (item.engineer ? `${projectDescription || 'Fixed Price Service'} - ${item.engineer}` : projectDescription || 'Fixed Price Service')
                    : item.is_fixed_monthly
                    ? withPeriod(`${projectDescription || 'Monthly Service'} - ${item.engineer}`, periodRange)
                    : withPeriod(`${projectDescription || 'Engineering Labor Hours'} - ${item.engineer}`, weekRange(item.week_ending))
                  }
                </td>
                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>
                  {item.is_adjustment ? (item.rate ? `${cs}${item.rate.toFixed(2)}` : `${cs}${item.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`) : item.is_consolidated ? `${cs}${item.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}` : item.is_fixed_price ? 'Fixed' : item.is_fixed_monthly ? 'Monthly' : `${cs}${item.rate?.toFixed(2) || '0.00'}`}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}></td>
                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>{cs}{item.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
              </tr>
            ))}
            {inv.lineItems.some(item => item.hours > 0) && inv.lineItems.length > 1 && (
              <tr style={{ borderTop: '2px solid #000', fontWeight: 'bold' }}>
                <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                  {inv.lineItems.reduce((sum, item) => sum + (item.hours || 0), 0).toFixed(2)}
                </td>
                <td style={{ border: '1px solid #ccc', padding: '8px' }} colSpan={4}>Total Hours</td>
                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>
                  {cs}{inv.lineItems.reduce((sum, item) => sum + (item.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
              </tr>
            )}
            </>
          ) : (
            <tr>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{inv.total_hours?.toFixed(2) || '0.00'}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>{poNumber || 'Engineering'}</td>
              <td style={{ border: '1px solid #ccc', padding: '8px' }}>
                {withPeriod(projectDescription || 'Engineering Labor Hours', periodRange)}
              </td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>—</td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}></td>
              <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>{cs}{inv.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: 200 }}>
          <tbody>
            <tr>
              <td style={{ padding: '4px 12px', textAlign: 'right' }}>Subtotal</td>
              <td style={{ padding: '4px 12px', textAlign: 'right', fontWeight: 'bold' }}>
                {cs}{inv.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '4px 12px', textAlign: 'right' }}>Sales tax</td>
              <td style={{ padding: '4px 12px', textAlign: 'right' }}>{cs}0.00</td>
            </tr>
            <tr style={{ borderTop: '2px solid #000' }}>
              <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 'bold', fontSize: 14 }}>Total</td>
              <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 'bold', fontSize: 14 }}>
                {cs}{inv.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {inv.print_notes && (
        <div style={{ marginTop: 16, padding: '8px 12px', border: '1px solid #ccc', borderRadius: 4, fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          <strong>Notes:</strong>{'\n'}{inv.print_notes}
        </div>
      )}
    </div>
  );
}
